import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { DetailsCard, useCan } from "components";
import { CustomerNoteModal, CustomerNoteRow } from "components/CustomerModal/components/CustomerNotes/components";
import "components/CustomerModal/components/CustomerNotes/CustomerNotes.scss";

import type { CUSTOMER_TYPES, Customer, CustomerNote, DealerLocation } from "models";

type CustomerNotesProps = {
  customer?: Customer;
  location?: DealerLocation | null;
  customerType?: CUSTOMER_TYPES.Driver | CUSTOMER_TYPES.Owner | CUSTOMER_TYPES.Contractor;
};

export const CustomerNotes = ({ customer, location, customerType }: CustomerNotesProps) => {
  const { t } = useTranslation();

  const canCreateCustomerNote = useCan("create", "customer-notes");

  const hasCustomerNotes = Boolean(customer?.notes?.length);
  const hasManualCustomerNote = Boolean(customer?.notes?.some(note => !note.dms_nr));

  const [showCustomerNoteDetails, setShowCustomerNoteDetails] = useState(hasCustomerNotes);
  const [selectedCustomerNote, setSelectedCustomerNote] = useState<CustomerNote | null>(null);
  const [showCreateCustomerNoteModal, setShowCreateCustomerNoteModal] = useState(false);

  const handleResetCustomerNote = () => setSelectedCustomerNote(null);
  const handleSelectCustomerNote = (note: CustomerNote) => setSelectedCustomerNote(note);
  const handleToggleCustomerNoteDetails = () => setShowCustomerNoteDetails(prev => !prev);
  const handleShowCreateCustomerNoteModal = () => setShowCreateCustomerNoteModal(true);
  const handleCloseCreateCustomerNoteModal = () => setShowCreateCustomerNoteModal(false);

  useEffect(() => {
    setShowCustomerNoteDetails(hasCustomerNotes);
  }, [customer]);

  if (!customer) return null;

  return (
    <div className="CustomerNotes">
      <DetailsCard
        icon="note sticky"
        containerStyle="mt-20"
        title={`${t("v8_notes", "Notes")} ${hasCustomerNotes ? `(${customer.notes?.length})` : ""} `}
        showExpandArrow={false}
        rightContent={
          canCreateCustomerNote &&
          !hasManualCustomerNote && (
            <>
              <Icon className={`chevron ${showCustomerNoteDetails ? "down" : "right"} pointer`} onClick={handleToggleCustomerNoteDetails} />
              {location?.is_customer_note_enabled && (
                <Button className="-appointment-status button-note" size="small" onClick={handleShowCreateCustomerNoteModal}>
                  <Icon name="plus" color="green" size="big" />
                </Button>
              )}
            </>
          )
        }
      >
        {customer.notes
          ?.toSorted((noteA, noteB) => (noteB.dms_nr ? 1 : 0) - (noteA.dms_nr ? 1 : 0))
          .map(note => (
            <CustomerNoteRow key={note.id} t={t} customerNote={note} onSelectNote={() => handleSelectCustomerNote(note)} customerType={customerType} />
          ))}
      </DetailsCard>

      <CustomerNoteModal
        location={location}
        customer={customer}
        selectedCustomerNote={selectedCustomerNote}
        open={Boolean(selectedCustomerNote || showCreateCustomerNoteModal)}
        onUnselectCustomerNote={handleResetCustomerNote}
        onCloseCreateCustomerNoteModal={handleCloseCreateCustomerNoteModal}
      />
    </div>
  );
};
